import {BalanceResponse, ShrikeHelper} from '@/helpers/ShrikeHelper'
import {MyWalletTokensTableItem} from '@/model/resource/MyWalletTokensTableItem'
import {TokensTableItemsCollection} from '@/model/collection/TokensTableItemsCollection'
import {NeoHelper} from './NeoHelper'
import {$} from '@/facade'
import Vue from 'vue'
import {GasCalculatorTargetCollection} from '@/model/collection/GasCalculatorTargetCollection'
import {PriceHistoryCollection} from '@/model/collection/PriceHistoryCollection'
import {GMNftAssetCollection} from '@/model/collection/GhostMarket/GMNftAssetCollection'
import {GMNftCollectionCollection} from '@/model/collection/GhostMarket/GMNftCollectionCollection'
import {GasCalculatorTarget} from '@/model/resource/GasCalculatorTarget'

type WalletInfo = {
  network: 'Neo3' | 'NeoX'
  isWatchWallet: boolean
  unclaimedGas: string
  fee: string
  balances: BalanceResponse[]
  tokenAssets: MyWalletTokensTableItem[]
  tokenLastTimeUpdated: string
  nftLastTimeUpdated: string
  nftAssetCollection: GMNftAssetCollection
  nftCollectionCollection: GMNftCollectionCollection
  candidateVoted: GasCalculatorTarget
}

export class MyWalletCacheAssist {
  tokensTableItemsCollection = new TokensTableItemsCollection().noPagination()
  gasCalculatorTargetCollection = new GasCalculatorTargetCollection().noPagination()
  priceHistoryCollection = new PriceHistoryCollection().noPagination()

  connectedAddress = ''

  isRefreshing = false
  isLoading = false
  isMobile = false

  defaultWallet: WalletInfo = {
    network: 'Neo3',
    isWatchWallet: false,
    unclaimedGas: '',
    fee: '',
    balances: [],
    tokenAssets: [],
    tokenLastTimeUpdated: '',
    nftLastTimeUpdated: '',
    nftAssetCollection: new GMNftAssetCollection(),
    nftCollectionCollection: new GMNftCollectionCollection(),
    candidateVoted: new GasCalculatorTarget(),
  }

  private cacheWallet: Record<string, WalletInfo> = {}

  constructor(isMobile: boolean) {
    this.isMobile = isMobile
    this.tokensTableItemsCollection.minimumLiquidityPool = null
  }

  get gasPriceInDollar() {
    return Number(this.priceHistoryCollection.items[0]?.currentPrice) ?? 0
  }

  get candidateVoted() {
    return this.currentWallet.candidateVoted
  }

  set candidateVoted(candidate: GasCalculatorTarget) {
    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      candidateVoted: candidate,
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }

  get currentWallet(): WalletInfo {
    return this.cacheWallet[this.connectedAddress] || this.defaultWallet
  }

  get walletBalanceFormatted(): string {
    return this.currentWallet.tokenAssets
      ?.reduce((acc, item) => acc + item.amountInDollar, 0)
      .toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
  }

  get neoBalance(): number {
    const neoToken = this.currentWallet.balances?.find(
      item => item.token.symbol === 'NEO'
    )

    return Number(neoToken?.amount ?? 0)
  }

  async refreshData() {
    if (!this.connectedAddress) {
      return
    }

    Vue.set(this, 'isRefreshing', true)

    const isWatchWallet = this.currentWallet.isWatchWallet

    if (isWatchWallet) {
      await this.populateWatchWallet(this.connectedAddress, true)
    } else {
      await this.populateWallet(this.connectedAddress, true)
    }

    Vue.set(this, 'isRefreshing', false)
  }

  async populateWallet(address: string, remakeCache: boolean = false) {
    $.await.init('populateMyWalletAssets')

    Vue.set(this, 'isLoading', true)

    const hasCachedWallet = !!this.cacheWallet[address]

    this.connectedAddress = address

    const isN3Address = NeoHelper.isValidNeo3Address(address)

    Vue.set(this.cacheWallet, address, {
      ...this.cacheWallet[this.connectedAddress],
      network: isN3Address ? 'Neo3' : 'NeoX',
      isWatchWallet: false,
    })

    if (hasCachedWallet && !remakeCache) {
      this.connectedAddress = address

      Vue.nextTick(() => {
        this.connectedAddress = address
      })
    } else {
      Vue.set(this.cacheWallet, address, {
        ...this.defaultWallet,
        network: isN3Address ? 'Neo3' : 'NeoX',
        isWatchWallet: false,
      })

      if (isN3Address) {
        await this.populateN3()

        await this.waitUntilN3WalletPopulated(address)
      } else {
        await this.populateX()

        await this.waitUntilXWalletPopulated(address)
      }
    }

    Vue.set(this, 'isLoading', false)

    $.await.done('populateMyWalletAssets')
  }

  async populateWatchWallet(address: string, remakeCache: boolean = false) {
    if (!address) {
      $.toast.abort('Address is required.')
      return
    }

    const isN3Address = NeoHelper.isValidNeo3Address(address)
    const isXAddress = NeoHelper.isValidNeoXAddress(address)

    if (!isN3Address && !isXAddress) {
      $.toast.abort('Invalid address, please try again.')
      return
    }

    $.await.init('populateMyWalletAssets')

    $.watchWallet.setAddress(address)

    Vue.set(this, 'isLoading', true)

    const hasCachedWallet = !!this.cacheWallet[address]
    if (hasCachedWallet && !remakeCache) {
      this.connectedAddress = ''

      this.connectedAddress = address

      Vue.set(this.cacheWallet[address], 'isWatchWallet', true)
    } else {
      this.connectedAddress = address

      Vue.set(this.cacheWallet, address, {
        ...this.defaultWallet,
        isWatchWallet: true,
        network: isN3Address ? 'Neo3' : 'NeoX',
      })

      if (isN3Address) {
        await this.populateN3()

        await this.waitUntilN3WalletPopulated(address)
      } else {
        await this.populateX()

        await this.waitUntilXWalletPopulated(address)
      }
    }

    Vue.set(this, 'isLoading', false)

    $.await.done('populateMyWalletAssets')
  }

  private async waitUntilN3WalletPopulated(address: string, timeout = 5000) {
    const start = Date.now()

    while (!this.isN3WalletPopulated(address)) {
      if (Date.now() - start > timeout) {
        break
      }

      await $.utils.sleep(100)
    }
  }

  private async waitUntilXWalletPopulated(address: string, timeout = 5000) {
    const start = Date.now()

    while (!this.isXWalletPopulated(address)) {
      if (Date.now() - start > timeout) {
        break
      }

      await $.utils.sleep(100)
    }
  }

  private isN3WalletPopulated(address: string): boolean {
    const wallet = this.cacheWallet[address]

    if (!wallet) return false

    return (
      Array.isArray(wallet.balances) &&
      wallet.balances.length > 0 &&
      wallet.candidateVoted !== null &&
      wallet.fee !== '' &&
      Array.isArray(wallet.nftAssetCollection?.items) &&
      wallet.nftAssetCollection.items.length > 0 &&
      Array.isArray(wallet.nftCollectionCollection?.items) &&
      wallet.nftCollectionCollection.items.length > 0 &&
      wallet.nftLastTimeUpdated !== '' &&
      Array.isArray(wallet.tokenAssets) &&
      wallet.tokenAssets.length > 0 &&
      wallet.tokenLastTimeUpdated !== '' &&
      wallet.unclaimedGas !== ''
    )
  }

  private isXWalletPopulated(address: string): boolean {
    const wallet = this.cacheWallet[address]
    if (!wallet) return false

    return (
      Array.isArray(wallet.balances) &&
      wallet.balances.length > 0 &&
      Array.isArray(wallet.nftAssetCollection?.items) &&
      wallet.nftAssetCollection.items.length > 0 &&
      Array.isArray(wallet.nftCollectionCollection?.items) &&
      wallet.nftCollectionCollection.items.length > 0 &&
      wallet.nftLastTimeUpdated !== '' &&
      Array.isArray(wallet.tokenAssets) &&
      wallet.tokenAssets.length > 0 &&
      wallet.tokenLastTimeUpdated !== ''
    )
  }

  private async populateN3() {
    this.tokensTableItemsCollection.networkIdentifier = 'neo3/mainnet'

    this.priceHistoryCollection.withoutTotal = true
    this.priceHistoryCollection.symbols = ['GAS']

    const mainPromise: Promise<unknown>[] = [
      this.tokensTableItemsCollection.queryAsPage(),
      this.priceHistoryCollection.queryAsPage(),
    ]
    await Promise.allSettled(mainPromise)

    this.gasCalculatorTargetCollection.withoutTotal = true

    const secondaryPromise: Promise<unknown>[] = [
      this.populateN3Balance(),
      this.populateN3UnclaimedGas(),
      this.populateN3UnclaimedGasFee(),
      this.gasCalculatorTargetCollection.queryAsPage(),
    ]
    await Promise.allSettled(secondaryPromise)

    this.populateTokens()

    const tertiaryPromise: Promise<unknown>[] = [
      this.populateN3CandidateVoted(),
      this.populateNft('n3'),
    ]
    await Promise.allSettled(tertiaryPromise)
  }

  private async populateX() {
    this.tokensTableItemsCollection.networkIdentifier = 'neox/mainnet'

    this.priceHistoryCollection.withoutTotal = true
    this.priceHistoryCollection.symbols = ['GAS']

    const mainPromise: Promise<unknown>[] = [
      this.tokensTableItemsCollection.queryAsPage(),
      this.priceHistoryCollection.queryAsPage(),
    ]
    await Promise.allSettled(mainPromise)

    const secondaryPromise: Promise<unknown>[] = [
      this.populateXBalance(),
      this.populateNft('neox'),
    ]
    await Promise.allSettled(secondaryPromise)

    this.populateTokens()
  }

  private populateTokens() {
    const walletTokenAssets: MyWalletTokensTableItem[] = []

    this.currentWallet.balances.forEach(balance => {
      if (balance.amount === '0') return

      const token = this.tokensTableItemsCollection.items.find(
        token =>
          token.marketInformation?.hash?.toLowerCase() ===
          balance.token.hash.toLowerCase()
      )

      if (!token) return

      const tokenItem = new MyWalletTokensTableItem()
      tokenItem.imageUrl = token.imageUrl
      tokenItem.symbol = token.symbol
      tokenItem.marketInformation = token.marketInformation
      tokenItem.blockchainVersion = token.blockchainVersion
      tokenItem.amount = Number(balance.amount)

      walletTokenAssets.push(tokenItem)
    })

    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      tokenLastTimeUpdated: this.tokensTableItemsCollection
        .lastTimeUpdatedFormatted,
      tokenAssets: walletTokenAssets,
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }

  private async populateNft(network: string) {
    this.currentWallet.nftAssetCollection.limit = this.isMobile ? 6 : 15
    this.currentWallet.nftAssetCollection.ownersChains = network
    this.currentWallet.nftAssetCollection.ownerAddress = this.connectedAddress
    this.currentWallet.nftAssetCollection.orderBy = 'MintDate'
    this.currentWallet.nftAssetCollection.orderDirection = 'Desc'

    await this.currentWallet.nftAssetCollection.queryAsPage()

    this.currentWallet.nftCollectionCollection.ownersChains = network
    this.currentWallet.nftCollectionCollection.ownerAddress = this.connectedAddress

    await this.currentWallet.nftCollectionCollection.queryAsPage()

    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      nftLastTimeUpdated: new Date().toLocaleString(),
      nftAssetCollection: this.currentWallet.nftAssetCollection,
      nftCollectionCollection: this.currentWallet.nftCollectionCollection,
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }

  private async populateN3Balance() {
    const walletBalance = await ShrikeHelper.getBalance(this.connectedAddress)

    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      balances: walletBalance,
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }

  private async populateXBalance() {
    const walletBalance = await NeoHelper.getNeoXBalance(this.connectedAddress)

    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      balances: walletBalance,
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }

  private async populateN3CandidateVoted() {
    const candidatePublicKey = await NeoHelper.getVoterData(
      this.connectedAddress
    )

    let currentCandidateVoted: GasCalculatorTarget | null = null

    const isNoCandidate = candidatePublicKey === ''
    if (isNoCandidate) {
      currentCandidateVoted = this.gasCalculatorTargetCollection
        .sortedCandidates[0]
    } else {
      currentCandidateVoted =
        this.gasCalculatorTargetCollection.sortedCandidates.find(
          candidate => candidatePublicKey === `0${candidate.publicKey}`
        ) ?? null
    }

    if (currentCandidateVoted) {
      const updatedWallet = {
        ...this.cacheWallet[this.connectedAddress],
        candidateVoted: currentCandidateVoted,
      }

      Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
    }
  }

  private async populateN3UnclaimedGas() {
    const {unclaimed} = await NeoHelper.getUnclaimedGasData(
      this.connectedAddress
    )

    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      unclaimedGas: (unclaimed * 10 ** -8).toFixed(8),
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }

  private async populateN3UnclaimedGasFee() {
    const {total} = await NeoHelper.calculateUnclaimedGasFee(
      this.connectedAddress
    )

    const updatedWallet = {
      ...this.cacheWallet[this.connectedAddress],
      fee: total.toString(),
    }

    Vue.set(this.cacheWallet, this.connectedAddress, updatedWallet)
  }
}
